import { useAtom } from 'jotai';
import { useCallback } from 'react';
import { VerificationControls } from 'components/verification-controls';
import { verificationStateAtomFamily } from 'lib/atoms/verification';

interface NRIVerificationProps {
  nriId: string;
  initialStatus: boolean | null;
  initialCommentsCount: number;
  eventsHref: string;
  commentsHref: string;
  disableActions: boolean;
}

export const NRIVerification = ({
  nriId,
  initialStatus,
  initialCommentsCount,
  eventsHref,
  commentsHref,
  disableActions,
}: NRIVerificationProps) => {
  const [persistedState, setPersistedState] = useAtom(
    verificationStateAtomFamily(nriId)
  );

  // Use persisted state if available, otherwise fall back to initial values
  const status = persistedState?.status ?? initialStatus;
  const commentsCount = persistedState?.commentsCount ?? initialCommentsCount;

  const handleStatusChange = useCallback(
    (newStatus: boolean | null) => {
      setPersistedState((prev) => {
        // Only update if status actually changed
        if (prev?.status === newStatus) return prev;
        return {
          status: newStatus,
          commentsCount: prev?.commentsCount ?? initialCommentsCount,
        };
      });
    },
    [setPersistedState, initialCommentsCount]
  );

  const handleCommentsCountChange = useCallback(
    (newCount: number) => {
      setPersistedState((prev) => {
        // Only update if count actually changed
        if (prev?.commentsCount === newCount) return prev;
        return {
          status: prev?.status ?? initialStatus,
          commentsCount: newCount,
        };
      });
    },
    [setPersistedState, initialStatus]
  );

  return (
    <VerificationControls
      path={[]}
      skipModal
      eventsHref={eventsHref}
      commentsHref={commentsHref}
      disableActions={disableActions}
      initialStatus={status}
      initialCommentsCount={commentsCount}
      onStatusChange={handleStatusChange}
      onCommentsCountChange={handleCommentsCountChange}
    />
  );
};
